import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, Users, Package, Loader2, Calendar,
  ArrowUp, ArrowDown, Minus, Wrench, AlertTriangle, CheckCircle2
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell
} from "recharts";
import { API } from "../../App";
import LostAndFoundPanel from "./LostAndFoundPanel";

const COLORS = ["#f97316", "#3b82f6", "#22c55e", "#eab308", "#8b5cf6", "#ec4899"];

// Minimum pulls required before we trust the loss_rate_pct enough to
// SHOW it. Backend still returns the real number regardless of sample
// size — this floor is purely a display-side judgement about statistical
// noise. A 100%-of-1-pull tech is technically the "highest rate" on the
// page but tells you nothing about a real pattern; muting these rows
// keeps the sort honest without hiding the underlying data.
// Tune here, not in the backend, so the API stays deterministic and
// the threshold can be raised/lowered without a redeploy of the wire.
const MIN_PULLS_FOR_RATE = 5;

const StockAnalytics = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30");
  const [summary, setSummary] = useState(null);
  const [staffComparison, setStaffComparison] = useState([]);
  const [trends, setTrends] = useState([]);
  const [productStats, setProductStats] = useState([]);
  // Write-offs summary — separate endpoint, admin-only. Independent of
  // the main analytics fetch so a slow/failing /write-offs call can't
  // block the rest of the analytics grid from rendering.
  const [writeOffSummary, setWriteOffSummary] = useState(null);

  // --- Backfill state ---
  // Two-tap flow:
  //   1) Tap "Backfill from Job Logs"  → fires dry_run=true, opens a
  //      confirmation dialog showing "N rows will be inserted".
  //   2) Tap "Confirm & run backfill"  → fires dry_run=false against
  //      the CURRENT server (preview or production, whichever the
  //      user is signed into). Safe: idempotent, source_ref keyed.
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillPlan, setBackfillPlan] = useState(null);   // dry-run summary
  const [backfillResult, setBackfillResult] = useState(null); // real-run summary

  // --- Material-cost backfill state ---
  // Second, independent two-tap flow — identical pattern to the usage-logs
  // backfill above but hitting POST /stock/analytics/backfill-material-costs.
  // Kept as a SEPARATE dialog + state block on purpose: the two backfills
  // scan different things, have different skip semantics, and must be
  // runnable independently. Sharing state would mean a preview from one
  // could bleed into the other's confirm click.
  //
  // ORDERING NOTE (also surfaced in the button tooltip + dialog copy):
  // this must run BEFORE the "Backfill historical margins" button on the
  // Job Profitability tab — that endpoint reads `material_cost` and just
  // keeps skipping any job where this hasn't been backfilled yet.
  const [matBackfillOpen, setMatBackfillOpen] = useState(false);
  const [matBackfillLoading, setMatBackfillLoading] = useState(false);
  const [matBackfillPlan, setMatBackfillPlan] = useState(null);
  const [matBackfillResult, setMatBackfillResult] = useState(null);

  // --- Job-duration backfill state ---
  // Third, independent two-tap flow — hits
  // POST /stock/analytics/backfill-job-durations. Same rules as the
  // material-cost block above: separate state so previews can't bleed
  // between the three backfills, and copy makes the ordering explicit
  // (materials → durations → Job Profitability's "Backfill historical
  // margins"). Duration and material-cost backfills are independent of
  // each other; both should complete before the final profitability
  // pass so hourly-pay labour_cost has both inputs it needs.
  const [durBackfillOpen, setDurBackfillOpen] = useState(false);
  const [durBackfillLoading, setDurBackfillLoading] = useState(false);
  const [durBackfillPlan, setDurBackfillPlan] = useState(null);
  const [durBackfillResult, setDurBackfillResult] = useState(null);

  const loadAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      const [summaryRes, staffRes, trendsRes, productsRes] = await Promise.all([
        axios.get(`${API}/stock/analytics/summary?days=${timeRange}`),
        axios.get(`${API}/stock/analytics/staff-comparison?days=${timeRange}`),
        axios.get(`${API}/stock/analytics/usage-trends?days=${timeRange}`),
        axios.get(`${API}/stock/analytics/product-stats?days=${timeRange}`)
      ]);

      setSummary(summaryRes.data);
      setStaffComparison(staffRes.data.staff_comparison || []);
      setTrends(trendsRes.data.trends || []);
      setProductStats(productsRes.data.product_stats || []);
      // Write-offs are best-effort: fetch alongside the rest but don't
      // fail the whole grid if this endpoint hiccups. The main analytics
      // block is the priority render — write-off summary is additive.
      axios
        .get(`${API}/stock/write-offs`, { params: { days: Number(timeRange) } })
        .then((res) => setWriteOffSummary(res.data))
        .catch(() => setWriteOffSummary(null));
    } catch (error) {
      console.error("Failed to load analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // --- Backfill handlers -----------------------------------------------------
  // Step 1 — always dry-run first. Even if the endpoint's default changes
  // in future, we hard-code `dry_run=true` on this call so a click can
  // never mutate data.
  const openBackfillPreview = async () => {
    setBackfillLoading(true);
    setBackfillPlan(null);
    setBackfillResult(null);
    setBackfillOpen(true);
    try {
      const res = await axios.post(
        `${API}/stock/analytics/backfill-usage-logs?dry_run=true`,
      );
      setBackfillPlan(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Backfill preview failed: ${detail}`);
      setBackfillOpen(false);
    } finally {
      setBackfillLoading(false);
    }
  };

  // Step 2 — real mutation. Requires the dry-run to have completed AND
  // the user to tap "Confirm & run backfill". Idempotent server-side so
  // an accidental double-tap is safe.
  const confirmBackfill = async () => {
    if (!backfillPlan) return;
    setBackfillLoading(true);
    try {
      const res = await axios.post(
        `${API}/stock/analytics/backfill-usage-logs?dry_run=false`,
      );
      setBackfillResult(res.data);
      toast.success(
        `Backfill complete — ${res.data.inserted} row${res.data.inserted === 1 ? "" : "s"} inserted`,
      );
      // Refresh analytics so the newly-inserted rows show up
      // immediately without requiring a manual reload.
      await loadAnalytics();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Backfill failed: ${detail}`);
    } finally {
      setBackfillLoading(false);
    }
  };

  const closeBackfill = () => {
    if (backfillLoading) return; // don't allow closing mid-mutation
    setBackfillOpen(false);
    setBackfillPlan(null);
    setBackfillResult(null);
  };

  // --- Material-cost backfill handlers ---------------------------------------
  // Mirrors the trio above exactly. Kept as separate fns (rather than a
  // parametrised helper) so the endpoint URLs, toast copy, and refresh
  // behaviour stay obvious at the call site — this is admin-facing
  // plumbing, not a place to be clever with abstractions.
  const openMatBackfillPreview = async () => {
    setMatBackfillLoading(true);
    setMatBackfillPlan(null);
    setMatBackfillResult(null);
    setMatBackfillOpen(true);
    try {
      const res = await axios.post(
        `${API}/stock/analytics/backfill-material-costs?dry_run=true`,
      );
      setMatBackfillPlan(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Material-cost preview failed: ${detail}`);
      setMatBackfillOpen(false);
    } finally {
      setMatBackfillLoading(false);
    }
  };

  const confirmMatBackfill = async () => {
    if (!matBackfillPlan) return;
    setMatBackfillLoading(true);
    try {
      const res = await axios.post(
        `${API}/stock/analytics/backfill-material-costs?dry_run=false`,
      );
      setMatBackfillResult(res.data);
      toast.success(
        `Material costs backfilled — ${res.data.updated} entr${res.data.updated === 1 ? "y" : "ies"} updated`,
      );
      // Analytics feed doesn't depend on material_cost directly, so no
      // refresh is required here — Job Profitability is the consumer that
      // benefits, and it reloads on its own tab visit.
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Material-cost backfill failed: ${detail}`);
    } finally {
      setMatBackfillLoading(false);
    }
  };

  const closeMatBackfill = () => {
    if (matBackfillLoading) return;
    setMatBackfillOpen(false);
    setMatBackfillPlan(null);
    setMatBackfillResult(null);
  };

  // --- Job-duration backfill handlers ----------------------------------------
  // Mirrors the trio above exactly. Kept separate on purpose so the
  // endpoint URL, toast copy, and refresh behaviour stay obvious at
  // each call site.
  const openDurBackfillPreview = async () => {
    setDurBackfillLoading(true);
    setDurBackfillPlan(null);
    setDurBackfillResult(null);
    setDurBackfillOpen(true);
    try {
      const res = await axios.post(
        `${API}/stock/analytics/backfill-job-durations?dry_run=true`,
      );
      setDurBackfillPlan(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Duration preview failed: ${detail}`);
      setDurBackfillOpen(false);
    } finally {
      setDurBackfillLoading(false);
    }
  };

  const confirmDurBackfill = async () => {
    if (!durBackfillPlan) return;
    setDurBackfillLoading(true);
    try {
      const res = await axios.post(
        `${API}/stock/analytics/backfill-job-durations?dry_run=false`,
      );
      setDurBackfillResult(res.data);
      toast.success(
        `Durations backfilled — ${res.data.updated} job${res.data.updated === 1 ? "" : "s"} updated`,
      );
      // Stock analytics feed doesn't depend on duration_minutes — the
      // downstream consumer is Job Profitability, which reloads on its
      // own tab visit. No refresh required here.
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Duration backfill failed: ${detail}`);
    } finally {
      setDurBackfillLoading(false);
    }
  };

  const closeDurBackfill = () => {
    if (durBackfillLoading) return;
    setDurBackfillOpen(false);
    setDurBackfillPlan(null);
    setDurBackfillResult(null);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IE', { day: '2-digit', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  // Category colors for pie chart
  const categoryData = summary?.by_category ? Object.entries(summary.by_category).map(([name, data], idx) => ({
    name,
    value: data.count,
    total: data.total,
    unit: data.unit
  })) : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-saturn-orange" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-saturn-orange" />
            Stock Usage Analytics
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Track material usage, compare staff performance, and identify trends
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={openMatBackfillPreview}
            disabled={matBackfillLoading}
            className="border-slate-700 text-slate-300 hover:text-white"
            data-testid="stock-backfill-material-costs-btn"
            title="Retroactively fill material_cost on historical job logs using the price now recorded on each stock unit. Run this BEFORE the 'Backfill historical margins' button on the Job Profitability tab — that one skips any job with no material_cost."
          >
            <Wrench className="w-4 h-4 mr-2" />
            Backfill material costs
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openDurBackfillPreview}
            disabled={durBackfillLoading}
            className="border-slate-700 text-slate-300 hover:text-white"
            data-testid="stock-backfill-job-durations-btn"
            title="Retroactively fill duration_minutes on historical job logs using job_log.started_at → job_log.start_time → booking.job_start_time. Run this AFTER 'Backfill material costs' and BEFORE the Job Profitability tab's 'Backfill historical margins' so hourly-pay labour_cost picks up the recovered durations."
          >
            <Wrench className="w-4 h-4 mr-2" />
            Backfill job durations
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openBackfillPreview}
            disabled={backfillLoading}
            className="border-slate-700 text-slate-300 hover:text-white"
            data-testid="stock-backfill-btn"
            title="Rebuild stock_usage_logs from job_logs.materials_used[] — one-time fix for historical data"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Backfill from Job Logs
          </Button>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Jobs</p>
                <p className="text-3xl font-bold text-white">{summary?.total_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-saturn-orange/20 flex items-center justify-center">
                <Package className="w-6 h-6 text-saturn-orange" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Usage Events</p>
                <p className="text-3xl font-bold text-white">{summary?.total_usage_events || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Active Staff</p>
                <p className="text-3xl font-bold text-white">{summary?.by_staff?.length || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Products Used</p>
                <p className="text-3xl font-bold text-white">{summary?.top_products?.length || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Materials Cost — only shown when at least one job has cost data */}
      {summary?.materials_cost?.jobs_with_cost_data > 0 && (
        <Card
          className="bg-slate-800/50 border-slate-700"
          data-testid="materials-cost-panel"
        >
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              Materials Cost — last {timeRange} days
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Total spent</p>
                <p className="text-2xl font-bold text-emerald-400 tabular-nums" data-testid="materials-cost-total-period">
                  €{(summary.materials_cost.total_material_cost || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Avg per job</p>
                <p className="text-2xl font-bold text-white tabular-nums">
                  {summary.materials_cost.avg_material_cost_per_job !== null
                    ? `€${summary.materials_cost.avg_material_cost_per_job.toFixed(2)}`
                    : "—"}
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  {summary.materials_cost.jobs_with_cost_data} job(s) with cost data
                  {summary.materials_cost.jobs_without_cost_data > 0 && (
                    <>
                      {" · "}
                      <span className="text-amber-500/80">
                        {summary.materials_cost.jobs_without_cost_data} without
                      </span>
                    </>
                  )}
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-slate-400 text-xs uppercase tracking-wider">Top cost driver</p>
                <p className="text-lg font-semibold text-white truncate">
                  {summary.materials_cost.top_products_by_cost?.[0]?.product_name || "—"}
                </p>
                <p className="text-slate-500 text-xs">
                  {summary.materials_cost.top_products_by_cost?.[0]
                    ? `€${summary.materials_cost.top_products_by_cost[0].total_cost.toFixed(2)} across ${summary.materials_cost.top_products_by_cost[0].usage_count} use(s)`
                    : ""}
                </p>
              </div>
            </div>

            {/* Top 5 materials by cost */}
            {summary.materials_cost.top_products_by_cost?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="materials-cost-table">
                  <thead>
                    <tr className="border-b border-slate-700/50 text-slate-400">
                      <th className="text-left py-2 px-2">Material</th>
                      <th className="text-right py-2 px-2">Uses</th>
                      <th className="text-right py-2 px-2">Avg / use</th>
                      <th className="text-right py-2 px-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.materials_cost.top_products_by_cost.map((r, i) => (
                      <tr key={i} className="border-b border-slate-800/40">
                        <td className="py-2 px-2 text-white">{r.product_name}</td>
                        <td className="py-2 px-2 text-right text-slate-300 tabular-nums">{r.usage_count}</td>
                        <td className="py-2 px-2 text-right text-slate-300 tabular-nums">
                          {r.avg_cost_per_use !== null ? `€${r.avg_cost_per_use.toFixed(2)}` : "—"}
                        </td>
                        <td className="py-2 px-2 text-right text-emerald-400 font-medium tabular-nums">
                          €{r.total_cost.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Staff Comparison Chart */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-saturn-orange" />
              Staff Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            {staffComparison.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={staffComparison} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis dataKey="name" type="category" stroke="#9ca3af" width={100} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Legend />
                  <Bar dataKey="films_meters" name="Films (m)" fill="#f97316" />
                  <Bar dataKey="chemicals_ml" name="Chemicals (ml)" fill="#3b82f6" />
                  <Bar dataKey="consumables_pcs" name="Consumables" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-500">
                No usage data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage by Category Pie Chart */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-saturn-orange" />
              Usage by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name.split('/')[0]} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {categoryData.map((entry) => (
                      <Cell key={`cell-${entry.name}`} fill={COLORS[categoryData.indexOf(entry) % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    formatter={(value, name, props) => [`${value} events`, props.payload.name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-500">
                No category data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Usage Trends Chart */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-saturn-orange" />
            Daily Usage Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trends.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9ca3af" 
                  tickFormatter={formatDate}
                  interval="preserveStartEnd"
                />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  labelFormatter={formatDate}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="films_m" 
                  name="Films (m)" 
                  stroke="#f97316" 
                  strokeWidth={2}
                  dot={{ fill: '#f97316', strokeWidth: 2 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="total_events" 
                  name="Total Events" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-500">
              No trend data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Stats Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-saturn-orange" />
            Product Usage Statistics (Min / Max / Avg per Job)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {productStats.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Product</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Category</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Jobs</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Total Used</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">
                      <span className="flex items-center justify-center gap-1">
                        <ArrowDown className="w-3 h-3 text-green-500" />
                        Min
                      </span>
                    </th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">
                      <span className="flex items-center justify-center gap-1">
                        <ArrowUp className="w-3 h-3 text-red-500" />
                        Max
                      </span>
                    </th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">
                      <span className="flex items-center justify-center gap-1">
                        <Minus className="w-3 h-3 text-blue-500" />
                        Avg
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {productStats.map((product) => (
                    <tr key={product.product_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 px-4 text-white">{product.product_name}</td>
                      <td className="py-3 px-4 text-slate-400 text-sm">{product.category}</td>
                      <td className="py-3 px-4 text-center text-saturn-orange font-medium">{product.total_jobs}</td>
                      <td className="py-3 px-4 text-center text-white">{product.total_display}</td>
                      <td className="py-3 px-4 text-center text-green-400">{product.min_display}</td>
                      <td className="py-3 px-4 text-center text-red-400">{product.max_display}</td>
                      <td className="py-3 px-4 text-center text-blue-400 font-medium">{product.avg_display}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center text-slate-500">
              No product usage data available for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Staff Performance Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-saturn-orange" />
            Staff Performance Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {staffComparison.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Staff Member</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Jobs</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Usage Events</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Films (m)</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Chemicals (ml)</th>
                    <th className="text-center py-3 px-4 text-slate-400 font-medium">Consumables</th>
                  </tr>
                </thead>
                <tbody>
                  {staffComparison.map((staff) => (
                    <tr key={staff.technician_id || staff.name} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 px-4 text-white font-medium">{staff.name}</td>
                      <td className="py-3 px-4 text-center text-saturn-orange font-bold">{staff.total_jobs}</td>
                      <td className="py-3 px-4 text-center text-slate-300">{staff.total_events}</td>
                      <td className="py-3 px-4 text-center text-orange-400">{staff.films_meters}m</td>
                      <td className="py-3 px-4 text-center text-blue-400">{staff.chemicals_ml}ml</td>
                      <td className="py-3 px-4 text-center text-green-400">{staff.consumables_pcs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center text-slate-500">
              No staff usage data available for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* --- Write-offs summary + Lost & Found (Part 3) ------------------
          Two additive admin surfaces for the stock-write-off feature.
          Both are best-effort renders — a failing /write-offs fetch
          leaves `writeOffSummary` null and the card gracefully hides
          itself so the rest of Analytics stays usable. */}
      {writeOffSummary && (
        <Card className="bg-slate-900 border-slate-700" data-testid="writeoffs-summary-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Write-offs · last {writeOffSummary.window_days}d
              <span className="text-xs font-normal text-slate-400">
                ({writeOffSummary.summary.total} total, {writeOffSummary.summary.open_count} open)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              {/* By reason */}
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">By reason</p>
                {Object.keys(writeOffSummary.summary.by_reason || {}).length === 0 ? (
                  <p className="text-slate-500">—</p>
                ) : (
                  <div className="space-y-1">
                    {Object.entries(writeOffSummary.summary.by_reason).map(([reason, count]) => (
                      <div
                        key={reason}
                        className="flex justify-between text-slate-300"
                        data-testid={`writeoff-by-reason-${reason}`}
                      >
                        <span className="capitalize">{reason}</span>
                        <span className="font-mono text-slate-100">{count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* By product (top 5) */}
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">Top products</p>
                {(writeOffSummary.summary.by_product || []).length === 0 ? (
                  <p className="text-slate-500">—</p>
                ) : (
                  <div className="space-y-1">
                    {writeOffSummary.summary.by_product.slice(0, 5).map((p) => (
                      <div
                        key={p.product_id}
                        className="flex justify-between text-slate-300 gap-2"
                        data-testid={`writeoff-by-product-${p.product_id}`}
                      >
                        <span className="truncate">{p.product_name || p.product_id}</span>
                        <span className="font-mono text-slate-100">{p.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* By technician — surfacing pattern, not a KPI. Layout
                  copy avoids any wording that implies fault or ranking.
                  Backend sorts by loss_rate_pct (nulls-last) so a
                  high-volume tech doesn't show up top just because they
                  handle more tools. We then re-sort client-side into
                  three tiers so a below-floor rate can't dominate the
                  top of the list on noise alone:
                    tier 1 — total_pulls >= MIN_PULLS_FOR_RATE (trustable rate, by rate desc)
                    tier 2 — 0 < total_pulls < floor (rate exists but noisy, by count desc)
                    tier 3 — total_pulls == 0 or rate is null (by count desc) */}
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">
                  Logged by (pattern-spotting only)
                </p>
                {(() => {
                  const rows = writeOffSummary.summary.by_technician || [];
                  if (rows.length === 0) {
                    return <p className="text-slate-500">—</p>;
                  }
                  const tier = (r) => {
                    if (r.loss_rate_pct === null || r.loss_rate_pct === undefined) return 2;
                    if ((r.total_pulls ?? 0) < MIN_PULLS_FOR_RATE) return 1;
                    return 0;
                  };
                  const sorted = [...rows].sort((a, b) => {
                    const ta = tier(a);
                    const tb = tier(b);
                    if (ta !== tb) return ta - tb;         // tier asc (0 first)
                    // Within tier: real-rate tier → by rate desc; other
                    // tiers → by count desc (their rate is either
                    // untrustable or null, so the raw count is the only
                    // signal left).
                    if (ta === 0) return (b.loss_rate_pct ?? 0) - (a.loss_rate_pct ?? 0);
                    return (b.count ?? 0) - (a.count ?? 0);
                  });
                  return (
                    <div className="space-y-1">
                      {sorted.slice(0, 5).map((t) => {
                        const totalPulls = t.total_pulls ?? 0;
                        const belowFloor =
                          t.loss_rate_pct !== null && t.loss_rate_pct !== undefined
                          && totalPulls < MIN_PULLS_FOR_RATE;
                        const hasRate = !belowFloor && (t.loss_rate_pct !== null && t.loss_rate_pct !== undefined);
                        const tooltip = belowFloor
                          ? `Rate hidden until at least ${MIN_PULLS_FOR_RATE} pulls in this window — ${totalPulls} pull${totalPulls === 1 ? "" : "s"} isn't enough to be meaningful.`
                          : hasRate
                            ? `Sorted by loss-rate — ${t.loss_rate_pct}% of ${totalPulls} pulls.`
                            : `No pulls in this window — rate can't be computed.`;
                        return (
                          <div
                            key={t.technician_id}
                            className={`flex justify-between items-baseline gap-2 ${belowFloor ? "text-slate-500" : "text-slate-300"}`}
                            data-testid={`writeoff-by-tech-${t.technician_id}`}
                            title={tooltip}
                          >
                            <span className="truncate">{t.technician_name || t.technician_id || "—"}</span>
                            <span className="text-right whitespace-nowrap font-mono text-[11px]">
                              <span
                                className={belowFloor ? "text-slate-500" : "text-slate-100"}
                                data-testid={`writeoff-by-tech-${t.technician_id}-count`}
                              >
                                {t.count}
                              </span>
                              {belowFloor ? (
                                <span
                                  className="text-slate-500 italic"
                                  data-testid={`writeoff-by-tech-${t.technician_id}-nodata`}
                                >
                                  {" "}(not enough data · {totalPulls} pull{totalPulls === 1 ? "" : "s"})
                                </span>
                              ) : (
                                <>
                                  <span className="text-slate-500"> (</span>
                                  <span
                                    className={hasRate ? "text-amber-300" : "text-slate-500"}
                                    data-testid={`writeoff-by-tech-${t.technician_id}-rate`}
                                  >
                                    {hasRate ? `${t.loss_rate_pct}%` : "—"}
                                  </span>
                                  <span className="text-slate-500"> of {totalPulls})</span>
                                </>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <LostAndFoundPanel />

      {/* --- Backfill dialog ---------------------------------------------
          Two-tap flow, described inline in the handler comments above.
          Never fires a mutation on the first tap — the initial call is
          always `dry_run=true` and the user has to explicitly hit
          "Confirm & run backfill" to write. -----------------------------*/}
      <Dialog open={backfillOpen} onOpenChange={(v) => { if (!v) closeBackfill(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="stock-backfill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-400" />
              Backfill Stock Usage Logs
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Rebuild the analytics feed from historical job logs. Safe,
              idempotent, and dry-runs first — nothing is written until
              you confirm.
            </DialogDescription>
          </DialogHeader>

          {backfillLoading && !backfillPlan && (
            <div className="py-6 flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <p className="text-slate-400 text-sm">Scanning job logs…</p>
            </div>
          )}

          {backfillPlan && !backfillResult && (
            <div className="space-y-3 text-sm">
              <div className="rounded border border-amber-500/30 bg-amber-500/10 p-3 text-amber-100">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="w-4 h-4" /> Plan (dry-run, nothing written yet)
                </div>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-amber-200/80">Job logs scanned</span>
                  <span className="text-right font-mono" data-testid="backfill-plan-scanned">{backfillPlan.scanned_job_logs}</span>
                  <span className="text-amber-200/80">Will insert</span>
                  <span className="text-right font-mono font-bold text-amber-100" data-testid="backfill-plan-to-insert">{backfillPlan.to_insert}</span>
                  <span className="text-amber-200/80">Already present (skipped)</span>
                  <span className="text-right font-mono" data-testid="backfill-plan-already-present">{backfillPlan.already_present}</span>
                  <span className="text-amber-200/80">Excluded (no product_id)</span>
                  <span className="text-right font-mono" data-testid="backfill-plan-skipped">{backfillPlan.skipped_no_product_id}</span>
                </div>
              </div>

              {backfillPlan.sample?.length > 0 && (
                <div className="rounded border border-slate-700 bg-slate-800/50 p-3">
                  <p className="text-xs text-slate-400 mb-2 font-semibold">First rows to insert:</p>
                  <ul className="space-y-1 text-xs text-slate-300">
                    {backfillPlan.sample.map((s, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">{s.product_name || s.product_id}</span>
                        <span className="text-slate-500 font-mono">{s.amount_used} {s.base_unit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {backfillPlan.to_insert === 0 && (
                <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-200 text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Nothing to backfill — every job log entry is already tracked.
                </div>
              )}
            </div>
          )}

          {backfillResult && (
            <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-100">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <CheckCircle2 className="w-4 h-4" /> Backfill complete
              </div>
              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <span className="text-emerald-200/80">Rows inserted</span>
                <span className="text-right font-mono font-bold" data-testid="backfill-result-inserted">{backfillResult.inserted}</span>
                <span className="text-emerald-200/80">Already present</span>
                <span className="text-right font-mono">{backfillResult.already_present}</span>
                <span className="text-emerald-200/80">Excluded (no product_id)</span>
                <span className="text-right font-mono">{backfillResult.skipped_no_product_id}</span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeBackfill} disabled={backfillLoading} data-testid="backfill-cancel-btn" className="border-slate-700 text-slate-300">
              {backfillResult ? "Close" : "Cancel"}
            </Button>
            {backfillPlan && !backfillResult && (
              <Button
                onClick={confirmBackfill}
                disabled={backfillLoading || backfillPlan.to_insert === 0}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                data-testid="backfill-confirm-btn"
              >
                {backfillLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
                ) : backfillPlan.to_insert === 0 ? (
                  "Nothing to run"
                ) : (
                  `Confirm & insert ${backfillPlan.to_insert} row${backfillPlan.to_insert === 1 ? "" : "s"}`
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Material-cost backfill dialog ---------------------------------
          Same two-tap pattern as the usage-logs dialog above. Copy makes
          the ordering explicit: this must run BEFORE the Job Profitability
          tab's own backfill so historical jobs actually pick up their
          costs instead of getting parked in skipped_no_material_cost
          forever. --------------------------------------------------------*/}
      <Dialog open={matBackfillOpen} onOpenChange={(v) => { if (!v) closeMatBackfill(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="stock-backfill-material-costs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-400" />
              Backfill Material Costs
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Re-derive <span className="font-mono">material_cost</span> on
              older job logs using the price now recorded on each stock
              unit. Safe, idempotent, dry-runs first — nothing is written
              until you confirm.
            </DialogDescription>
          </DialogHeader>

          {/* Ordering nudge — surfaces the sequencing every time the
              dialog opens so the run order doesn't get accidentally
              reversed. Same amber tone used for cautionary copy elsewhere. */}
          <div className="rounded border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-100 flex gap-2 items-start" data-testid="mat-backfill-order-note">
            <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-amber-200">Run this first</div>
              <div className="text-amber-200/80 mt-0.5">
                Complete this backfill BEFORE the &quot;Backfill historical
                margins&quot; button on the Job Profitability tab — that one
                skips any job where material_cost hasn&apos;t been populated
                yet.
              </div>
            </div>
          </div>

          {matBackfillLoading && !matBackfillPlan && (
            <div className="py-6 flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <p className="text-slate-400 text-sm">Scanning job logs…</p>
            </div>
          )}

          {matBackfillPlan && !matBackfillResult && (
            <div className="space-y-3 text-sm">
              <div className="rounded border border-amber-500/30 bg-amber-500/10 p-3 text-amber-100">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="w-4 h-4" /> Plan (dry-run, nothing written yet)
                </div>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-amber-200/80">Job logs scanned</span>
                  <span className="text-right font-mono" data-testid="mat-backfill-plan-scanned-jobs">{matBackfillPlan.scanned_job_logs}</span>
                  <span className="text-amber-200/80">Material entries scanned</span>
                  <span className="text-right font-mono" data-testid="mat-backfill-plan-scanned-entries">{matBackfillPlan.scanned_material_entries}</span>
                  <span className="text-amber-200/80">Will update</span>
                  <span className="text-right font-mono font-bold text-amber-100" data-testid="mat-backfill-plan-to-update">{matBackfillPlan.to_update}</span>
                  <span className="text-amber-200/80">Already costed (skipped)</span>
                  <span className="text-right font-mono" data-testid="mat-backfill-plan-already-costed">{matBackfillPlan.already_costed}</span>
                  <span className="text-amber-200/80">Skipped — no unit reference</span>
                  <span className="text-right font-mono" data-testid="mat-backfill-plan-no-unit-id">{matBackfillPlan.skipped_no_unit_id}</span>
                  <span className="text-amber-200/80">Skipped — unit no longer exists</span>
                  <span className="text-right font-mono" data-testid="mat-backfill-plan-unit-not-found">{matBackfillPlan.skipped_unit_not_found}</span>
                  <span className="text-amber-200/80">Skipped — unit still uncosted</span>
                  <span className="text-right font-mono" data-testid="mat-backfill-plan-still-uncosted">{matBackfillPlan.skipped_still_uncosted}</span>
                </div>
              </div>

              {/* Still-uncosted nudge — this counter will NOT move until
                  someone prices the underlying product in Inventory. Make
                  that explicit so admins don't assume the tool is broken. */}
              {matBackfillPlan.skipped_still_uncosted > 0 && (
                <div className="rounded border border-slate-700 bg-slate-800/50 p-3 text-xs text-slate-300 flex gap-2 items-start" data-testid="mat-backfill-still-uncosted-nudge">
                  <Wrench className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-amber-200">
                      {matBackfillPlan.skipped_still_uncosted} entr{matBackfillPlan.skipped_still_uncosted === 1 ? "y" : "ies"} still need a product priced in Inventory first
                    </div>
                    <div className="text-slate-400 mt-0.5">
                      Set <span className="font-mono">cost</span> on the referenced stock unit (via Inventory &rarr; Backfill Costs) then run this again to include them.
                    </div>
                  </div>
                </div>
              )}

              {matBackfillPlan.sample?.length > 0 && (
                <div className="rounded border border-slate-700 bg-slate-800/50 p-3">
                  <p className="text-xs text-slate-400 mb-2 font-semibold">First entries to update:</p>
                  <ul className="space-y-1 text-xs text-slate-300">
                    {matBackfillPlan.sample.map((s, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">
                          {s.product_name || "—"} · {s.booking_id}
                        </span>
                        <span className="text-slate-500 font-mono">
                          {Number(s.amount_used).toFixed(2)} · €{Number(s.material_cost).toFixed(4)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {matBackfillPlan.to_update === 0 && (
                <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-200 text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Nothing to backfill — every eligible entry already has a material cost recorded.
                </div>
              )}
            </div>
          )}

          {matBackfillResult && (
            <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-100">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <CheckCircle2 className="w-4 h-4" /> Backfill complete
              </div>
              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <span className="text-emerald-200/80">Entries updated</span>
                <span className="text-right font-mono font-bold" data-testid="mat-backfill-result-updated">{matBackfillResult.updated}</span>
                <span className="text-emerald-200/80">Already costed</span>
                <span className="text-right font-mono">{matBackfillResult.already_costed}</span>
                <span className="text-emerald-200/80">Skipped — no unit reference</span>
                <span className="text-right font-mono">{matBackfillResult.skipped_no_unit_id}</span>
                <span className="text-emerald-200/80">Skipped — unit no longer exists</span>
                <span className="text-right font-mono">{matBackfillResult.skipped_unit_not_found}</span>
                <span className="text-emerald-200/80">Skipped — unit still uncosted</span>
                <span className="text-right font-mono">{matBackfillResult.skipped_still_uncosted}</span>
              </div>
              {matBackfillResult.updated > 0 && (
                <div className="mt-2 text-[11px] text-emerald-200/80">
                  Next step: open the <span className="font-semibold">Job Profitability</span> tab and run &quot;Backfill historical margins&quot; to compute margins on the jobs you just healed.
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeMatBackfill} disabled={matBackfillLoading} data-testid="mat-backfill-cancel-btn" className="border-slate-700 text-slate-300">
              {matBackfillResult ? "Close" : "Cancel"}
            </Button>
            {matBackfillPlan && !matBackfillResult && (
              <Button
                onClick={confirmMatBackfill}
                disabled={matBackfillLoading || matBackfillPlan.to_update === 0}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                data-testid="mat-backfill-confirm-btn"
              >
                {matBackfillLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
                ) : matBackfillPlan.to_update === 0 ? (
                  "Nothing to run"
                ) : (
                  `Confirm & update ${matBackfillPlan.to_update} entr${matBackfillPlan.to_update === 1 ? "y" : "ies"}`
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Job-duration backfill dialog ---------------------------------
          Third instance of the two-tap pattern. Ordering callout uses
          the same amber tone as the material-cost dialog. Duration and
          material-cost backfills are independent of each other, but
          BOTH should complete before the final "Backfill historical
          margins" run on the Job Profitability tab — that's where
          hourly-pay labour_cost gets its inputs. -----------------------*/}
      <Dialog open={durBackfillOpen} onOpenChange={(v) => { if (!v) closeDurBackfill(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="stock-backfill-job-durations-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-400" />
              Backfill Job Durations
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Compute <span className="font-mono">duration_minutes</span> on
              older job logs that were completed while the live path
              was reading the wrong start-time source. Safe, idempotent,
              dry-runs first — nothing is written until you confirm.
            </DialogDescription>
          </DialogHeader>

          {/* Ordering nudge — matches the material-cost dialog's callout
              style. Anchors the correct run sequence every time the
              dialog opens so it can't drift over time. */}
          <div className="rounded border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-100 flex gap-2 items-start" data-testid="dur-backfill-order-note">
            <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-amber-200">Correct run order</div>
              <div className="text-amber-200/80 mt-0.5">
                <span className="font-semibold">Backfill material costs</span> &rarr;{" "}
                <span className="font-semibold">Backfill job durations</span> (this one) &rarr;
                Job Profitability&apos;s{" "}
                <span className="font-semibold">Backfill historical margins</span>.
                Materials and durations are independent, but both feed the
                final margin pass — run them first.
              </div>
            </div>
          </div>

          {durBackfillLoading && !durBackfillPlan && (
            <div className="py-6 flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <p className="text-slate-400 text-sm">Scanning job logs…</p>
            </div>
          )}

          {durBackfillPlan && !durBackfillResult && (
            <div className="space-y-3 text-sm">
              <div className="rounded border border-amber-500/30 bg-amber-500/10 p-3 text-amber-100">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="w-4 h-4" /> Plan (dry-run, nothing written yet)
                </div>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-amber-200/80">Job logs scanned</span>
                  <span className="text-right font-mono" data-testid="dur-backfill-plan-scanned">{durBackfillPlan.scanned_job_logs}</span>
                  <span className="text-amber-200/80">Will update</span>
                  <span className="text-right font-mono font-bold text-amber-100" data-testid="dur-backfill-plan-to-update">{durBackfillPlan.to_update}</span>
                  <span className="text-amber-200/80">Already has duration (skipped)</span>
                  <span className="text-right font-mono" data-testid="dur-backfill-plan-already">{durBackfillPlan.already_has_duration}</span>
                  <span className="text-amber-200/80">Skipped — no start time</span>
                  <span className="text-right font-mono" data-testid="dur-backfill-plan-no-start">{durBackfillPlan.skipped_no_start_time}</span>
                  <span className="text-amber-200/80">Skipped — no end time</span>
                  <span className="text-right font-mono" data-testid="dur-backfill-plan-no-end">{durBackfillPlan.skipped_no_end_time}</span>
                </div>
              </div>

              {/* No-start-time nudge — this is genuinely unrecoverable
                  (we don't estimate from checked_in_at or other proxies).
                  Make that explicit so admins don't file a bug expecting
                  it to eventually catch up. */}
              {durBackfillPlan.skipped_no_start_time > 0 && (
                <div className="rounded border border-slate-700 bg-slate-800/50 p-3 text-xs text-slate-300 flex gap-2 items-start" data-testid="dur-backfill-no-start-nudge">
                  <Wrench className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-amber-200">
                      {durBackfillPlan.skipped_no_start_time} job{durBackfillPlan.skipped_no_start_time === 1 ? "" : "s"} without a recoverable start time
                    </div>
                    <div className="text-slate-400 mt-0.5">
                      No <span className="font-mono">started_at</span>,{" "}
                      <span className="font-mono">start_time</span>, or booking{" "}
                      <span className="font-mono">job_start_time</span> exists — genuinely unrecoverable.
                      Duration will stay blank for these; going forward,
                      new jobs record the start on check-in so this
                      bucket won&apos;t grow.
                    </div>
                  </div>
                </div>
              )}

              {durBackfillPlan.sample?.length > 0 && (
                <div className="rounded border border-slate-700 bg-slate-800/50 p-3">
                  <p className="text-xs text-slate-400 mb-2 font-semibold">First jobs to update:</p>
                  <ul className="space-y-1 text-xs text-slate-300">
                    {durBackfillPlan.sample.map((s, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">
                          {s.booking_id}
                          <span className="text-slate-500 ml-2">({s.resolved_start_source})</span>
                        </span>
                        <span className="text-slate-500 font-mono">
                          {s.duration_minutes} min
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {durBackfillPlan.to_update === 0 && (
                <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-200 text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Nothing to backfill — every eligible job already has a duration recorded.
                </div>
              )}
            </div>
          )}

          {durBackfillResult && (
            <div className="rounded border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-100">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <CheckCircle2 className="w-4 h-4" /> Backfill complete
              </div>
              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <span className="text-emerald-200/80">Jobs updated</span>
                <span className="text-right font-mono font-bold" data-testid="dur-backfill-result-updated">{durBackfillResult.updated}</span>
                <span className="text-emerald-200/80">Already had duration</span>
                <span className="text-right font-mono">{durBackfillResult.already_has_duration}</span>
                <span className="text-emerald-200/80">Skipped — no start time</span>
                <span className="text-right font-mono">{durBackfillResult.skipped_no_start_time}</span>
                <span className="text-emerald-200/80">Skipped — no end time</span>
                <span className="text-right font-mono">{durBackfillResult.skipped_no_end_time}</span>
              </div>
              {durBackfillResult.updated > 0 && (
                <div className="mt-2 text-[11px] text-emerald-200/80">
                  Next step: open the <span className="font-semibold">Job Profitability</span> tab and run &quot;Backfill historical margins&quot; so any hourly-pay technicians pick up their newly-computable labour_cost.
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeDurBackfill} disabled={durBackfillLoading} data-testid="dur-backfill-cancel-btn" className="border-slate-700 text-slate-300">
              {durBackfillResult ? "Close" : "Cancel"}
            </Button>
            {durBackfillPlan && !durBackfillResult && (
              <Button
                onClick={confirmDurBackfill}
                disabled={durBackfillLoading || durBackfillPlan.to_update === 0}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                data-testid="dur-backfill-confirm-btn"
              >
                {durBackfillLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
                ) : durBackfillPlan.to_update === 0 ? (
                  "Nothing to run"
                ) : (
                  `Confirm & update ${durBackfillPlan.to_update} job${durBackfillPlan.to_update === 1 ? "" : "s"}`
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockAnalytics;
